// Library Methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  CalendarIcon,
  MachineIcon,
  ReportIcon,
  ServerIcon,
} from "src/assets/icons";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import {
  Bullets,
  Callout,
  Chapter,
  GUIDE_NAMESPACE,
  GuideTable,
  Paragraph,
  SubHeading,
  WhoRow,
  scrollToGuideSection,
} from "../GuideText/GuideText";
import { mockTileBackground } from "../GuideMocks/PortalMocks";
import {
  ConnectionsWalkthrough,
  EditPacWalkthrough,
  MachineDicomWalkthrough,
  MachineFolderWalkthrough,
  ReportsWalkthrough,
  WorklistCsvWalkthrough,
  WorklistPacWalkthrough,
} from "../Walkthroughs/ConnectWalkthroughs";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

const GroupCard = ({
  section,
  icon,
  title,
  description,
  options,
}: {
  section: string;
  icon: ReactNode;
  title: string;
  description: string;
  options: Array<string>;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { surface, mutedText, dividerBorder } = getSurfaceTokens(theme);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => scrollToGuideSection(section)}
      sx={{
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        padding: "16px",
        borderRadius: "8px",
        backgroundColor: surface,
        border: dividerBorder,
        "&:hover": { borderColor: ACCENT_TEAL },
        "&:focus-visible": { outline: "3px solid #35BCFD" },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap="8px"
        color={ACCENT_TEAL}
        fontWeight={700}
      >
        {icon}
        {t(title)}
      </Box>
      <Typography fontSize="13.5px" color={mutedText} lineHeight={1.5}>
        {t(description)}
      </Typography>
      <Box display="flex" flexWrap="wrap" gap="4px" fontSize="12.5px">
        {options.map((option) => (
          <Box
            key={option}
            component="span"
            sx={{
              borderRadius: "4px",
              padding: "1px 6px",
              backgroundColor: mockTileBackground(theme),
            }}
          >
            {t(option)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const GroupsChapter = () => (
  <Chapter id="groups" eyebrow="Connect things" title="The three groups">
    <Paragraph text="Click <b>Connections</b> on the hub card. This page tells the hub what to connect to. It has three groups, one for each kind of job:" />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: "12px",
      }}
    >
      <GroupCard
        section="worklist"
        icon={<CalendarIcon />}
        title="Worklist"
        description="Who is booked today, so each exam lands on the right patient."
        options={["PAC server", "Shared folder (CSV)"]}
      />
      <GroupCard
        section="machine"
        icon={<MachineIcon />}
        title="Machine"
        description="A machine sending its exams straight to the hub."
        options={["Over DICOM", "Files in a folder"]}
      />
      <GroupCard
        section="reports"
        icon={<ReportIcon />}
        title="RetinaLogik Exams"
        description="Where finished RetinaLogik reports go back to."
        options={["PAC server", "Shared folder"]}
      />
    </Box>
    <ConnectionsWalkthrough />
    <SubHeading text="Which ones do I need?" />
    <GuideTable
      columns={["Your clinic has...", "Set up"]}
      rows={[
        [
          "A PAC server (for example FORUM or Harmony) that already collects exams from your machines",
          "<b>Worklist</b> from the PAC server, with <b>Bring in their exams</b> turned on. No machines needed. Add <b>RetinaLogik Exams</b> to get reports back into it.",
        ],
        [
          "Machines but no PAC server",
          "A <b>Machine</b> for each one, plus a <b>Worklist</b> if your EMR can export one. Add <b>RetinaLogik Exams</b> to a folder if your EMR imports reports from a folder.",
        ],
        [
          "An EMR that exports today's schedule as a CSV file",
          "<b>Worklist</b> from a shared folder.",
        ],
        [
          "Both a PAC server and machines that do not send to it",
          "All of the above. They work side by side.",
        ],
      ]}
    />
    <Callout
      tone="tip"
      text="If the hub is shared with several of your clinics, each setup screen has a <b>Clinic</b> box. Pick the clinic the connection belongs to. A connection for another clinic shows up when you switch to that clinic."
    />
  </Chapter>
);

export const WorklistChapter = () => (
  <Chapter
    id="worklist"
    eyebrow="Group 1"
    icon={<CalendarIcon width="16px" height="16px" />}
    title="Worklist: who is booked today"
  >
    <Paragraph text="The worklist is your list of booked patients. The hub uses it to match each exam with the right patient. There are two ways to get it." />
    <SubHeading text="Option A: from a PAC server" />
    <WhoRow
      items={[
        { kind: "you", text: "You can do this" },
        { kind: "it", text: "IT gives you the server's address" },
      ]}
    />
    <Paragraph text="The hub asks your PAC server for today's appointments. It can then also ask the server for those patients' exams. That way you do not need to connect each machine." />
    <WorklistPacWalkthrough />
    <Paragraph text="<b>Already have this PAC server?</b> If it is already set up for reports, the second step shows a <b>PAC server</b> list. Pick it instead of <b>New DICOM PAC server</b> and you will not need to type the address again." />
    <Paragraph text="<b>How far back?</b> The first time a patient appears on the worklist, the hub can also bring their older exams: <i>nothing earlier</i>, <i>the last 6 months</i>, <i>the last year</i>, <i>the last 2 years</i> (the usual choice), <i>the last 5 years</i> or <i>all of it</i>." />
    <Callout
      tone="tip"
      text="The hub only brings exams for patients who are <b>on the worklist</b>. Exams of anyone else stay in your PAC server. Keep the worklist current so same-day bookings get their exams too."
    />
    <SubHeading text="Option B: from a shared folder (CSV file)" />
    <WhoRow
      items={[
        { kind: "you", text: "You can do this" },
        { kind: "it", text: "IT sets up the export from your EMR" },
      ]}
    />
    <Paragraph text="Your EMR saves the day's schedule as a <csv>CSV file</csv> in a folder. The hub reads it at the times you choose." />
    <WorklistCsvWalkthrough />
    <Paragraph text='<b>Advanced:</b> "Also read the next (days)" lets the hub read upcoming days too. 0 means today only, and the most is 14.' />
    <SubHeading text="What the CSV file must look like" />
    <Paragraph text="Give these rules to whoever sets up the export from your EMR:" />
    <Bullets
      items={[
        "One file per day, named with the date first, like <code>2026-10-19.csv</code>.",
        "One patient per line, with these columns in this order: patient ID, first name, middle name, last name, sex (M, F or O), birth date (MM/dd/yyyy), clinic name.",
      ]}
    />
    <Paragraph text="Once the worklist runs, its job shows how many patients it imported today. See <b>Patients imported today</b>." />
  </Chapter>
);

export const MachineChapter = () => (
  <Chapter
    id="machine"
    eyebrow="Group 2"
    icon={<MachineIcon width="16px" height="16px" />}
    title="Machine: exams straight from a device"
  >
    <Paragraph text="Use this when a machine sends its exams directly to the hub. If the machine already sends everything to your PAC server, skip this: its exams come in through the worklist's query." />
    <Paragraph text="The portal only lists machines RetinaLogik supports, and only offers what your model can do. A machine can send exams in one of two ways." />
    <SubHeading text="Option A: the machine sends exams to the hub (DICOM)" />
    <WhoRow
      items={[
        { kind: "you", text: "You fill in the portal" },
        {
          kind: "it",
          text: "the machine's technician enters the values on the machine",
        },
      ]}
    />
    <MachineDicomWalkthrough />
    <Callout
      tone="tip"
      text="<b>Copy for the technician</b> copies the four values in the dashed box. Send them to whoever sets up the machine. They enter them in the machine's network or DICOM settings. If the hub PC has more than one address, the portal says so: use the one on the machines' network."
    />
    <Callout
      tone="warning"
      text='If the portal says <b>"DICOM needs the vendor&apos;s software or licence"</b>, your machine can only send over DICOM once the maker turns that feature on. Ask the vendor, or choose <b>It saves files to a folder</b> instead.'
    />
    <SubHeading text="Option B: the machine saves files to a folder" />
    <WhoRow
      items={[
        { kind: "you", text: "You can do this" },
        { kind: "it", text: "IT shares the folder" },
      ]}
    />
    <Paragraph text="Many machines can export a PDF or DICOM file into a folder. The hub watches that folder and picks up each new file." />
    <MachineFolderWalkthrough />
    <Paragraph text="The hub accepts <b>PDF</b>, <b>DICOM</b> and <b>XML</b> files. When a machine saves a PDF and an XML with the same name, the hub keeps them together as one exam. Each folder can only be used by one connection." />
  </Chapter>
);

export const ReportsChapter = () => (
  <Chapter
    id="reports"
    eyebrow="Group 3"
    icon={<ReportIcon width="16px" height="16px" />}
    title="RetinaLogik Exams: reports back to your clinic"
  >
    <Paragraph text="When RetinaLogik finishes a report, the hub can put it back where your clinic looks for it. Click <ui>Add destination</ui> in the <b>RetinaLogik Exams</b> group." />
    <ReportsWalkthrough />
    <Bullets
      items={[
        "<b>PAC server:</b> the report is stored next to the original exam, so it shows up wherever your team already looks at exams. If the PAC server is not set up yet, choose <b>New DICOM PAC server</b> and type its address, as in the worklist steps.",
        "<b>Shared folder:</b> pick <b>PDF</b> (one file per report), <b>DICOM</b> (one file per report section) or both. File names never contain patient names.",
        "<b>Send reports of exams from:</b> only exams from this date onward get their reports sent. Today is the usual choice.",
      ]}
    />
  </Chapter>
);

const OwnAddressBadge = () => {
  const { t } = useTranslation();

  return (
    <Typography
      component="span"
      fontSize="11px"
      fontWeight={800}
      sx={{
        color: "#3B1F0B",
        backgroundColor: "#FFC48A",
        borderRadius: "999px",
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {t("Own address")}
    </Typography>
  );
};

export const EditPacChapter = () => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Chapter
      id="edit-pac"
      eyebrow="Connect things"
      icon={<ServerIcon width="16px" height="16px" />}
      title="Change a PAC server"
    >
      <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
      <Paragraph text="A PAC server can do three jobs: read the worklist, bring in exams, and receive reports. Click <ui>Edit</ui> on its card to switch each job on or off in one place. A job that is off shows <b>Turn on</b> on the card as a shortcut." />
      <EditPacWalkthrough />
      <Box
        component="ul"
        sx={{
          margin: 0,
          paddingLeft: "22px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Typography component="li" fontSize="14px" lineHeight={1.6}>
          <b>{t("More options")}</b>{" "}
          {t(
            "lets one job use a different address than the server's. That job then shows an"
          )}{" "}
          <OwnAddressBadge /> {t("badge.")}
        </Typography>
      </Box>
      <Bullets
        items={[
          "<b>Let FORUM push exams instead</b> is for archives that send exams to the hub on their own. Then every exam is sent, not just those of patients on the worklist.",
          '<b>Remove PAC server</b> asks once more: "This stops its worklist, exams and reports jobs." Exams already received stay in RetinaLogik.',
        ]}
      />
      <Callout
        tone="warning"
        text='Bringing in exams needs a worklist. If you turn the worklist off, the portal reminds you: "Exams are brought in only for patients on a worklist. Switch on the worklist, or add a worklist from a shared folder first."'
      />
    </Chapter>
  );
};
